import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Cards that change lane slide to their new lane instead of teleporting.
 *
 * The board's lanes are the step's state, so a card moves lane exactly when
 * somebody decides something: approve, send back, cancel. That decision is the
 * whole point of the page, and the old list answered it by silently reordering
 * two regions six hundred pixels apart, which is an answer a person has to go
 * and look for. The card moving is the receipt.
 *
 * FLIP, by hand, because the console carries no animation dependency and does
 * not want one for forty lines: measure every card before the commit, measure
 * again after layout, and for any card whose box moved, put it back where it was
 * with a transform and animate that transform away. The browser animates a
 * compositor-only property from a position the card is already in, so nothing
 * reflows and nothing flashes at the old position.
 *
 * Three things this deliberately does not do. It does not animate a card that
 * merely appeared (there is no "before" to come from, and a slide out of the
 * corner is a lie about where it was). It does not animate the first paint. And
 * it does nothing at all under `prefers-reduced-motion`, where the instant swap
 * is the correct behaviour rather than a degraded one.
 */

/** Long enough to read as one motion, short enough not to sit between two clicks. */
const LANE_MOVE_MS = 320;

/** Anything smaller is layout noise, not a lane change. */
const MOVED_PX = 2;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    // A browser without matchMedia, or one that throws on it, still gets a
    // working board: the cards simply move without animating.
    return true;
  }
}

export interface LaneFlip {
  /**
   * Ref callback for each card, keyed by the row's id. Pass the same id the
   * board keys the card on, so a card that changes lane is recognised as the
   * same card rather than as one leaving and another arriving.
   */
  card: (id: string) => (node: HTMLElement | null) => void;
}

export function useLaneFlip(): LaneFlip {
  const nodes = useRef(new Map<string, HTMLElement>());
  const boxes = useRef(new Map<string, DOMRect>());
  const first = useRef(true);

  const card = useCallback(
    (id: string) => (node: HTMLElement | null) => {
      if (node) nodes.current.set(id, node);
      else nodes.current.delete(id);
    },
    [],
  );

  useLayoutEffect(() => {
    const previous = boxes.current;
    const next = new Map<string, DOMRect>();
    // Measure everything first, then animate. Reading a box after writing a
    // transform to an earlier card would measure a layout that includes that
    // write, and the second card's delta would come out wrong.
    for (const [id, node] of nodes.current) next.set(id, node.getBoundingClientRect());
    boxes.current = next;

    if (first.current) {
      first.current = false;
      return;
    }
    if (prefersReducedMotion()) return;

    for (const [id, node] of nodes.current) {
      const before = previous.get(id);
      const after = next.get(id);
      if (!before || !after) continue;
      const dx = before.left - after.left;
      const dy = before.top - after.top;
      if (Math.abs(dx) < MOVED_PX && Math.abs(dy) < MOVED_PX) continue;
      node.animate(
        [
          { transform: `translate(${dx}px, ${dy}px)`, opacity: 0.7 },
          { transform: 'translate(0, 0)', opacity: 1 },
        ],
        { duration: LANE_MOVE_MS, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
      );
    }
  });

  return { card };
}
